import { createContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import RouteConstants from "../../../constants/routeConstants.json";
import ContentData from "../../../constants/contentFile.json";
import { handleApiError, StateOptions } from "../../../constants/Constants";
import { addDealerApi, bulkUploadApi } from "../../../api/Api";
import { useCookies } from "react-cookie";
// Validation utility functions
export const validateField = (name, value) => {
  switch (name) {
    case "customerCode":
      // Ensures the value is not empty and contains only numbers
      return /^\d+$/.test(value);

    case "customerName":
      return value?.trim().length > 0;

    case "mobile":
      // Validates 10-digit mobile number
      const mobileRegex = /^[6-9]\d{9}$/;
      return mobileRegex.test(value);

    case "region":
      return value?.trim().length > 0;

    case "district":
       // Validates district
       const districtRegex = /^[A-Za-z\s]+$/;
       return districtRegex.test(value);
 

    case "city":
      // Validates city
      const cityRegex = /^[a-zA-Z\s'-]{2,50}$/;
      return cityRegex.test(value);

    case "streetOrHouseNumber":
      // Validates address
      const addressRegex = /^[a-zA-Z0-9\s,'-./#]{3,100}$/;
      return addressRegex.test(value);

    case "postalCode":
      // Validates 6-digit postal code
      const postalRegex = /^[1-9][0-9]{5}$/;
      return postalRegex.test(value);

    default:
      return true;
  }
};

export const getErrorMessage = (name, value) => {
  switch (name) {
    case "customerCode":
      return value.trim().length === 0
        ? "Customer code is required"
        : "Customer code should contain only numbers";

    case "customerName":
      return "Customer name is required";

    case "mobile":
      return value.trim().length === 0
        ? "Mobile number is required"
        : "Please enter a valid 10-digit mobile number";

    case "region":
      return "Please select a region";

    case "district":
      return value.trim().length === 0
      ? "District is required"
      : "Please enter a valid district name";

    case "city":
      return value.trim().length === 0
      ? "City is required"
      : "Please enter a valid city name";

    case "streetOrHouseNumber":
      return value.trim().length === 0
      ? "Address is required"
      : "Please enter a valid address";

    case "postalCode":
      return value.trim().length === 0
        ? "Postal code is required"
        : "Please enter a valid 6-digit postal code";

    default:
      return "";
  }
};

export const AddDealerContext = createContext({});

export const AddDealerContextProvider = ({ children }) => {
  const [formData, setFormData] = useState({
    customerCode: "",
    customerName: "",
    mobile: "",
    region: "",
    district: "",
    city: "",
    streetOrHouseNumber: "",
    postalCode: "",
  });
  const [validationErrors, setValidationErrors] = useState({
    customerCode: "",
    customerName: "",
    mobile: "",
    region: "",
    district: "",
    city: "",
    streetOrHouseNumber: "",
    postalCode: "",
  });
  const [successAlertShow, setSuccessAlertShow] = useState(false);
  const [successAlertMessage, setSuccessAlertMessage] = useState("");
  const [regionId, setRegionId] = useState();
  const [spinnerStatus, setSpinnerStatus] = useState(false);
  const [errorPopup, setErrorPopup] = useState(false);
  const [errorPopupMessage, setErrorPopupMessage] = useState("");
  const [buttonText, setButtonText] = useState("");
  const [cookies, setCookie, removeCookie] = useCookies(["user"]);
  const [statusValue, setStatusValue] = useState(0);
  const [errorExcelUrl, setErrorExcelUrl] = useState("");
  // bulk upload
  const [showModal, setShowModal] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [errorCount, setErrorCount] = useState(null);
  const permissionAccess = JSON.parse(
    localStorage.getItem("permissions")
  )?.filter((item) => {
    return item.feature === "67b4165e2efc50c984271c80";
  })[0];
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Update form data
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    // Validate field and update error message
    const isValid = validateField(name, value);
    setValidationErrors((prevErrors) => ({
      ...prevErrors,
      [name]: isValid ? "" : getErrorMessage(name, value),
    }));
  };

  const goToDealerList = () => {
    navigate(RouteConstants.LIST_DEALER);
  };

  const handleSelectData = (e) => {
    const selectedRegionId = e.target.value;
    const selectedRegion = StateOptions.find(
      (region) => region.value === selectedRegionId
    );

    const regionValue = selectedRegion ? selectedRegion.label : "";
    setRegionId(selectedRegionId);
    setFormData((prevData) => ({
      ...prevData,
      region: regionValue,
    }));

    // Validate region selection
    setValidationErrors((prevErrors) => ({
      ...prevErrors,
      region: regionValue ? "" : getErrorMessage("region", ""),
    }));
  };

  const downloadTemplate = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    window.open(`${baseUrl}/DealerListSample.xlsx`, "_blank");
  };

  const bulkUpload = () => {
    setShowModal(true);
  };

  // add dealer -submit
  const addDealer = async (e) => {
    e.preventDefault();

    const newErrors = {};
    let hasErrors = false;

    Object.keys(formData).forEach((fieldName) => {
      const isValid = validateField(fieldName, formData[fieldName]);
      newErrors[fieldName] = isValid
        ? ""
        : getErrorMessage(fieldName, formData[fieldName]);
      if (!isValid) hasErrors = true;
    });

    setValidationErrors(newErrors);

    if (!hasErrors) {
      setSpinnerStatus(true);
      try {
        const response = await addDealerApi(formData);
        if (response.data.statusCode === 200) {
          setSpinnerStatus(false);
          setSuccessAlertShow(true);
          setSuccessAlertMessage(response?.data?.message);
          setErrorPopup(false);
        }
      } catch (error) {
        handleApiError(
          error,
          setStatusValue,
          setErrorPopupMessage,
          setErrorPopup,
          setButtonText
        );
      }
      setSpinnerStatus(false);
    }
  };

  const goToLogin = () => {
    navigate(RouteConstants.LOGIN);
    removeCookie("x-access-token");
  };
  // bulk upload
  const handleFileChange = (file) => {
    setUploadedFile(file);
    setErrorCount(null);
    setErrorExcelUrl("");
  };

  const handleBulkUploadSubmit = async () => {
    if (uploadedFile) {
      setSpinnerStatus(true); // Show spinner while uploading
      const formData = new FormData();
      formData.append("file", uploadedFile); // Append file correctly

      try {
        const response = await bulkUploadApi(formData);
        if (response.data.statusCode === 200) {
          setSpinnerStatus(false);
          if (response?.data?.data?.errorOccurred === true) {
            setErrorCount(response?.data?.data?.errorRowCount);
            setErrorExcelUrl(response?.data?.data?.errorFilePath);
            setSuccessAlertShow(false);
          } else {
            setShowModal(false);
            setSuccessAlertShow(true);
            setSuccessAlertMessage(response?.data?.message);
            setErrorPopup(false);
          }
        }
      } catch (error) {
        handleApiError(
          error,
          setStatusValue,
          setErrorPopupMessage,
          setErrorPopup,
          setButtonText
        );
      }
      setSpinnerStatus(false); // Stop spinner on error
    }
  };

  const handleCancel = () => {
    setUploadedFile(null);
    setShowModal(false);
    setErrorCount(null);
  };

  const handleDownloadExcel = () => {
    window.open(errorExcelUrl, "_blank");
  };
  const onClearFile = () => {
    setUploadedFile(null);
    setErrorCount(null);
    setErrorExcelUrl("");
  };

  // --------------------------------

  const providerValue = {
    formData,
    goToDealerList,
    handleChange,
    addDealer,
    successAlertMessage,
    successAlertShow,
    spinnerStatus,
    handleSelectData,
    bulkUpload,
    permissionAccess,
    downloadTemplate,
    handleDownloadExcel,
    onClearFile,
    handleCancel,
    handleFileChange,
    showModal,
    setShowModal,
    errorCount,
    validationErrors,
    regionId,
    errorPopup,
    errorPopupMessage,
    setErrorPopup,
    goToLogin,
    buttonText,
    statusValue,
    handleBulkUploadSubmit,
  };
  return (
    <AddDealerContext.Provider value={providerValue}>
      {children}
    </AddDealerContext.Provider>
  );
};
